import React, { useState, useEffect } from "react";
import api from "../../api";
import Select from "react-select";

const TeacherSubjectAssignPage = () => {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);

  const schoolId = localStorage.getItem("school_id");

  const teacherURL = `/teachers/list`;
  const assignURL = `/teacher-subjects/add`;
  const assignedListURL = `/teacher-subjects/list`;

  // 🟦 1. Fetch teachers, classes (initial)
  useEffect(() => {
    fetchTeachers();
    fetchClasses();
  }, []);

  // -------------------------------
  // 🔹 Fetch Teacher List
  const fetchTeachers = async () => {
    try {
      const res = await api.get(`${teacherURL}`);
      setTeachers(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching teachers:", err);
    }
  };

  // 🔹 Fetch Classes
  const fetchClasses = async () => {
    try {
      const res = await api.get(`/get-classes`);
      if (res.data?.success) setClasses(res.data.data);
    } catch {
      console.error("Failed to fetch classes");
    }
  };

  // 🔹 Fetch Sections (depends on class)
  const fetchSections = async (classId) => {
    if (!classId) return;
    try {
      const res = await api.get(`/get-sections/${classId}`);
      if (res.data?.success) setSections(res.data.data);
      else setSections([]);
    } catch {
      setSections([]);
    }
  };

  // 🔹 Fetch Subjects by Class + Section (NEW)
  const fetchSubjects = async (classId, sectionId) => {
    if (!classId || !sectionId) {
      setSubjects([]);
      return;
    }

    try {
      const res = await api.get(
        `/class-subjects/list?class_id=${classId}&section_id=${sectionId}`
      );

      if (res.data?.status) {
        setSubjects(res.data.data || []);
      } else {
        setSubjects([]);
      }
    } catch (err) {
      console.error("Error fetching class-section subjects:", err);
      setSubjects([]);
    }
  };

  // 🔹 Fetch Assigned Subjects
  const fetchAssignedSubjects = async (teacherId) => {
    if (!teacherId) return;
    try {
      const res = await api.get(
        `${assignedListURL}?teacher_id=${teacherId}`
      );
      setAssignedSubjects(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching assigned subjects:", err);
    }
  };

  // 🟦 2. Trigger subject list when class or section changes
    useEffect(() => {
    if (selectedClass && selectedSection) {
        fetchSubjects(selectedClass.value, selectedSection.value);
    } else {
        setSubjects([]);
        setSelectedSubjects([]); // ✅ clear previously selected subjects
    }
    }, [selectedClass, selectedSection]);


  // 🟦 3. Fetch assigned subjects when teacher changes
  useEffect(() => {
    if (selectedTeacher) {
      fetchAssignedSubjects(selectedTeacher.value);
    } else {
      setAssignedSubjects([]);
    }
  }, [selectedTeacher]);

  // -------------------------------
  // 🔹 Assign Subjects to Teacher
  const handleAssign = async (e) => {
    e.preventDefault();
    if (
      !selectedTeacher ||
      !selectedClass ||
      !selectedSection ||
      selectedSubjects.length === 0
    ) {
      setMsg({
        type: "error",
        text: "Select teacher, class, section & at least one subject",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        teacher_id: selectedTeacher.value,
        class_id: selectedClass.value,
        section_id: selectedSection.value,
        subject_ids: selectedSubjects.map((s) => parseInt(s.value, 10)), // ✅ force int
      };

      const res = await api.post(assignURL, payload);

      if (res.data?.status) {
        setMsg({ type: "success", text: res.data.message });
        fetchAssignedSubjects(selectedTeacher.value);
      } else {
        setMsg({ type: "error", text: res.data.message });
      }
    } catch (err) {
      setMsg({ type: "error", text: "Failed to assign subjects" });
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Delete Assigned Subject
  const handleDelete = async (subjectId) => {
    if (!selectedTeacher) {
      setMsg({ type: "error", text: "Select teacher first" });
      return;
    }

    try {
      const res = await api.post("/teacher-subjects/delete", {
        teacher_id: selectedTeacher.value,
        subject_id: subjectId,
      });

      if (res.data.status) {
        setMsg({ type: "success", text: res.data.message });
        fetchAssignedSubjects(selectedTeacher.value);
      } else {
        setMsg({ type: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("Delete error:", err);
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Failed to delete subject.",
      });
    }
  };

  // -------------------------------
  // 🔹 UI
  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, background: "#f4f6f8" }}>
        <main
          style={{
            padding: "30px",
            maxWidth: "900px",
            margin: "auto",
            marginTop: "0px",
          }}
        >
          <section style={cardStyle}>
            <h2 style={titleStyle}>📚 Assign Subjects to Teacher</h2>

            {msg && (
              <div
                style={{
                  marginBottom: 12,
                  color: msg.type === "error" ? "#cc0000" : "#166534",
                }}
              >
                {msg.text}
              </div>
            )}

            <form onSubmit={handleAssign}>
              <div style={formGrid}>
                <FormGroup label="Teacher">
                  <Select
                    options={teachers.map((t) => ({
                      value: t.id,
                      label: `${t.name} (${t.mobile})`,
                    }))}
                    value={selectedTeacher}
                    onChange={(selected) => setSelectedTeacher(selected)}
                    placeholder="Select teacher..."
                    isClearable
                    isSearchable
                  />
                </FormGroup>

                <FormGroup label="Class">
                  <Select
                    options={classes.map((c) => ({
                      value: c.id,
                      label: c.class_name,
                    }))}
                    value={selectedClass}
                    onChange={(selected) => {
                      setSelectedClass(selected);
                      fetchSections(selected?.value);
                      setSelectedSection(null);
                      setSubjects([]);
                      setSelectedSubjects([]);
                    }}
                    placeholder="Select class..."
                    isClearable
                  />
                </FormGroup>

                <FormGroup label="Section">
                  <Select
                    options={sections.map((s) => ({
                      value: s.id,
                      label: s.section_name,
                    }))}
                    value={selectedSection}
                    onChange={(selected) => setSelectedSection(selected)}
                    placeholder="Select section..."
                    isClearable
                    isDisabled={!selectedClass}
                  />
                </FormGroup>

                <FormGroup label="Subjects (Filtered by Class & Section)">
                  <Select
                    options={subjects.map((s) => ({
                      value: s.subject_id,
                      label: s.subject_name,
                    }))}
                    value={selectedSubjects}
                    onChange={(selected) =>
                      setSelectedSubjects(selected || [])
                    }
                    placeholder={
                      selectedClass && selectedSection
                        ? "Select subjects..."
                        : "Select class & section first"
                    }
                    isMulti
                    isClearable
                    isDisabled={!selectedClass || !selectedSection}
                  />
                </FormGroup>
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle} disabled={loading}>
                  {loading ? "Assigning..." : "Assign Subjects"}
                </button>
              </div>
            </form>
          </section>

          {selectedTeacher && (
            <section style={cardStyle}>
              <h2 style={titleStyle}>📋 Assigned Subjects</h2>
              {assignedSubjects.length === 0 ? (
                <p>No subjects assigned.</p>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Subject</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedSubjects.map((row, i) => (
                      <tr key={i}>
                        <td>{row.class_name}</td>
                        <td>{row.section_name}</td>
                        <td>{row.subject_name}</td>
                        <td>
                          <button
                            style={{ ...buttonStyle, background: "red" }}
                            onClick={() => handleDelete(row.subject_id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

// Shared Components
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "20px",
};

const FormGroup = ({ label, children }) => (
  <div>
    <label
      style={{
        marginBottom: "6px",
        display: "block",
        fontWeight: 500,
        color: "#444",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const buttonStyle = {
  padding: "8px 18px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  marginBottom: "30px",
};
const titleStyle = { color: "#0077cc", marginBottom: "25px" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };

export default TeacherSubjectAssignPage;
